import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import * as React from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { type ContentReferenceContext, UserTheme } from '@gitbook/app-database';
import { resolveNavigationScreenToURL } from '@gitbook/app-navigation';
import {
    BLOCK_IMAGE,
    type BlockImageData,
    type BlockImageSizeValue,
    type BlockImagesData,
    images,
} from '@gitbook/doc-core';
import type { HiveClient } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import {
    createPalette,
    hideOnDrag,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteHandle,
    PalettePositionWidth,
    useConfigurationTheme,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { type Block, type Document, type Fragment, TextRange } from '@gitbook/slate';
import type { BlockProps, EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    ProgressiveImage,
    Toolbar,
    type ToolbarItemProps,
    usePopoverTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { shortcuts } from '../../../Keyboard';
import { userThemeAtom } from '../../../state';
import { getImageResizedURL } from '../../../utils';
import {
    type ImageURISource,
    resolveContentRefCache,
    useContentReferenceContext,
    useImageContentReference,
} from '../../ContentReferences';
import { type DraggableState, getDragItem } from '../../DragAndDrop';
import { useMediaGallery } from '../../MediaGallery';
import { spaceLayout } from '../../Space';
import { useBlockLayoutMaxWidth } from '../BlockLayout';
import { type CaptionState, EditableCaption, useCaption } from '../caption';
import { useIsNodeEstimatedInViewport, VoidBlockFrame } from '../common';
import { ContentEditorContext } from '../contexts';
import { BlockImageVerticalDropLine } from './BlockImageVerticalDropLine';
import { EditAltTextDialog } from './EditAltTextModal';
import { ReplaceImageButton } from './ReplaceImageButton';
import { imageDimensionsAtom } from './state';

/**
 * Renderer for an image block.
 */
export function BlockImage(props: BlockProps): React.ReactElement {
    const { node, attributes, editor } = props;
    const { readOnly } = editor;
    const imageData = node.data.toFrozenObject<BlockImageData>();
    const { width, height, ref, refDark } = imageData;

    const source = useImageContentReference(ref, refDark);
    const drag = useDraggable({
        ...getDragItem({
            id: node.key,
            type: 'block',
            blockType: node.type,
            previewLabel: 'Move image',
        }),
    });

    // We need the up to date version of this node's `parent`
    // because the `parent` props is not updated when an image is dropped
    // in the parent block, which causes `isLast` to be wrong and the UI to be incorrect.
    const parent = editor.value.document.getParent(node.key) ?? props.parent;
    const index = parent.getChildIndex(node.key);
    const isFirst = index === 0;

    const { withFrame = false } = parent.data.toFrozenObject<BlockImagesData>();

    const onMoveImage = useEventCallback((imageIds: string[], position: 'before' | 'after') => {
        editor.change((change) => {
            imageIds.forEach((imageId) => {
                if (position === 'before') {
                    change.moveBeforeNodeByKey(imageId, node.key);
                } else {
                    change.moveAfterNodeByKey(imageId, node.key);
                }
            });
        });
    });

    const caption = useCaption(node, editor);

    return (
        <div className="relative flex">
            {isFirst && (
                <BlockImageVerticalDropLine
                    position="before"
                    onDrop={(imageIds) => onMoveImage(imageIds, 'before')}
                    editor={editor}
                />
            )}

            <div
                className={clsx(
                    'relative overflow-hidden',
                    withFrame && 'rounded-2xl border border-base'
                )}
            >
                {/* Frame grid */}
                {withFrame && (
                    <div
                        className={clsx(
                            '-top-0.5 -left-0.5 absolute inset-px opacity-40 dark:opacity-10',
                            'bg-[length:24px_24px,24px_24px] bg-repeat',
                            'bg-[linear-gradient(to_right,_rgb(234,235,238)_1px,_transparent_1px),linear-gradient(to_bottom,_rgb(234,235,238)_1px,_transparent_1px)]',
                            'dark:bg-[linear-gradient(to_right,_rgb(122,128,139)_1px,_transparent_1px),linear-gradient(to_bottom,_rgb(122,128,139)_1px,_transparent_1px)]'
                        )}
                    />
                )}

                {/* Shadow overlay */}
                {withFrame && (
                    <div
                        className={clsx(
                            'pointer-events-none absolute inset-0 rounded-2xl',
                            'shadow-[inset_0_0_10px_10px_rgba(255,255,255,0.9)]',
                            'dark:shadow-[inset_0_0_10px_10px_rgba(21,23,28,0.9)]'
                        )}
                    />
                )}

                <div
                    className={clsx(
                        'group/image-block relative size-full',
                        props.isSelected ? 'z-40' : 'hover:z-30',
                        withFrame && 'p-2'
                    )}
                >
                    <div
                        ref={drag.setNodeRef}
                        className="relative z-[50] flex flex-col items-center"
                        style={{
                            maxWidth: getImageDimension({ dimension: width, default: '100%' }),
                            maxHeight: getImageDimension({ dimension: height, default: '100%' }),
                        }}
                        {...attributes}
                    >
                        <VoidBlockFrame
                            {...props}
                            className="w-full"
                            isFocused={props.isFocused || props.isSelected}
                        >
                            {source ? (
                                <ClickableImage
                                    source={source}
                                    height={height}
                                    width={width}
                                    isDragging={drag.isDragging}
                                    blockProps={props}
                                    className={withFrame ? 'rounded-xl' : undefined}
                                />
                            ) : (
                                <BrokenImage
                                    editor={editor}
                                    node={node}
                                    className={withFrame ? 'rounded-xl' : undefined}
                                />
                            )}
                        </VoidBlockFrame>

                        {!readOnly && source ? (
                            <ImageOptions
                                {...props}
                                source={source}
                                imageData={imageData}
                                ref={drag.setActivatorNodeRef}
                                isDragging={drag.isDragging}
                                dragListeners={drag.listeners}
                                canBeDragged={parent.nodes.size > 1}
                                caption={caption}
                            />
                        ) : null}
                    </div>
                    <EditableCaption {...props} className={clsx(withFrame && 'z-[30] py-0.5')} />
                </div>
            </div>

            <BlockImageVerticalDropLine
                position="after"
                onDrop={(imageId) => onMoveImage(imageId, 'after')}
                editor={editor}
            />
        </div>
    );
}

function ClickableImage(props: {
    source: ImageURISource;
    height: BlockImageData['height'];
    width: BlockImageData['width'];
    blockProps: BlockProps;
    isDragging: boolean;
    className?: string;
}) {
    const {
        source,
        blockProps: { node, editor, parent },
    } = props;
    const imageHeight = getImageDimension({ dimension: props.height, default: 'auto' });
    const imageWidth = getImageDimension({ dimension: props.width, default: '100vw' });
    const withPriorityLoading = useIsNodeEstimatedInViewport(editor, node.key);

    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();
    const gallery = useMediaGallery();
    const theme = useConfigurationTheme();

    // When clicking, we zoom the image in a gallery
    const onPress = async () => {
        if (!source || !source.uri) {
            return;
        }

        // get all images in parent block and resolve them, to be used in the gallery.
        // we retrive here instead of passing from the parent to avoid unnecessary rerenders on each image update
        const imageItems = await getParentImagesInContext(
            hiveClient,
            parent,
            contentReferenceContext,
            theme.color
        );

        const startIndex = parent.getChildIndex(node.key);

        gallery.open(imageItems, startIndex);
    };

    const [imageDimensionsState, setImageDimensionsState] = useRecoilState(imageDimensionsAtom);
    const [complete, setComplete] = React.useState(false);
    const maxBlockWidth = useBlockLayoutMaxWidth();

    return (
        <AtomButton
            onPress={onPress}
            className="relative mx-auto flex cursor-zoom-in"
            style={{
                width: source.width ? `min(${source.width}px, 100%)` : '100%',
                height: imageHeight,
                maxWidth: imageWidth,
                aspectRatio:
                    source.width && source.height
                        ? `${source.width} / ${source.height}`
                        : undefined,
            }}
        >
            <ProgressiveImage
                onLoad={(e) => {
                    setComplete((e.target as HTMLImageElement).complete);
                    const { naturalWidth, naturalHeight } = e.target as HTMLImageElement;
                    if (!naturalWidth || !naturalHeight) {
                        return;
                    }
                    setImageDimensionsState((state) => ({
                        ...state,
                        [node.key]: {
                            width: naturalWidth,
                            height: naturalHeight,
                        },
                    }));
                }}
                src={getImageResizedURL(source.uri, { width: maxBlockWidth })}
                width={source.width ?? imageDimensionsState[node.key]?.width}
                height={source.height ?? imageDimensionsState[node.key]?.height}
                loading={withPriorityLoading ? 'eager' : 'lazy'}
                alt={images.getImageAltText(node)}
                rounded={false}
                className={clsx(
                    complete && 'w-full',
                    'h-auto',
                    props.isDragging && 'opacity-50',
                    props.className
                )}
                // We don't want to resize the image automatically,
                // instead we resize the image manually to the size of the editor.
                resize={false}
            />
        </AtomButton>
    );
}

/**
 * Content when the image is broken.
 */
function BrokenImage(props: { editor: EditorContainer; node: Block; className?: string }) {
    const { editor, node } = props;
    return (
        <EmptyState className={clsx('bg-standout-base', props.className)}>
            <EmptyStateIcon>
                <Icon.CloudOff />
            </EmptyStateIcon>
            <Heading>Could not load image</Heading>
            <Description>The image file may have been removed.</Description>
            <ReplaceImageButton
                label="Select new image"
                leadingIcon={Icon.Image}
                kind="secondary"
                editor={editor}
                node={node}
                className="mt-2"
                isBroken
            />
        </EmptyState>
    );
}

type ImageOptionsProps = BlockProps & {
    source: ImageURISource;
    imageData: BlockImageData;
    isDragging: boolean;
    dragListeners: DraggableState['listeners'];
    canBeDragged: boolean;
    caption: CaptionState;
};
/**
 * Image options menu.
 */
const ImageOptions = React.forwardRef<HTMLElement, ImageOptionsProps>(
    (props, ref: React.Ref<HTMLButtonElement>) => {
        const { editor, node, isDragging, dragListeners, canBeDragged } = props;

        const trigger = usePopoverTriggerState();

        const sizingPaletteAnchorRef = React.useRef<HTMLButtonElement>(null);
        const sizingPaletteHandle = usePaletteHandle(sizingPaletteAnchorRef);
        const isVisible =
            trigger.isOpen || props.isSelected || props.isFocused || sizingPaletteHandle.opened;

        const openToolbar = useEventCallback(() => {
            editor.change((change) => {
                // When opening the toolbar by clicking on the button,
                // we focus the block if it's not already focused.
                // We need this otherwise the toolbar shows only when the block is focused.
                if (
                    node &&
                    TextRange.isTextRange(change.value.selection) &&
                    !change.value.selection.hasEdgeIn(node)
                ) {
                    change.collapseToStartOf(node);
                }

                change.focus();
            });
            trigger.open();
        });

        const closeToolbar = useEventCallback(() => {
            trigger.close();
            if (sizingPaletteHandle.opened) {
                sizingPaletteHandle.close();
            }
        });

        const closeToolbarAndFocusEditor = useEventCallback(() => {
            closeToolbar();
            editor.change((change) => {
                change.focus();
            });
        });

        React.useEffect(() => {
            if (!props.isSelected && !props.isFocused) {
                closeToolbar();
            }
        }, [closeToolbar, props.isSelected, props.isFocused]);

        return (
            <PopoverRoot {...trigger.rootProps}>
                <PopoverTrigger>
                    <Button
                        label="Image options"
                        kind="secondary"
                        leadingIcon={canBeDragged ? Icon.GrabberVertical : Icon.Edit}
                        iconOnly
                        ref={ref}
                        {...(canBeDragged ? dragListeners : {})}
                        contentEditable={false}
                        tooltip={{
                            label: 'Click for options',
                            description: canBeDragged ? 'Drag to move' : undefined,
                            shortcut: canBeDragged ? shortcuts.EDITOR_MODIFIER_PALETTE : undefined,
                            disabled: isDragging,
                        }}
                        onPress={openToolbar}
                        className={clsx(
                            hideOnDrag,
                            !canBeDragged && !isDragging && 'cursor-pointer',
                            isDragging ? 'cursor-grabbing' : 'cursor-grab',
                            'absolute top-1 left-1 z-20 flex shrink-0 items-center gap-2 rounded border bg-base p-1 transition',
                            trigger.isOpen && 'invisible',
                            isVisible && props.isOnlyNodeSelected
                                ? 'opacity-100'
                                : 'opacity-0 group-hover/image-block:opacity-100'
                        )}
                    />
                </PopoverTrigger>
                <Popover
                    placement="right-start"
                    offset={-28 /* probably considered a hack */}
                    sticky
                >
                    <ImageOptionsToolbar
                        {...props}
                        isVisible={isVisible}
                        paletteHandle={sizingPaletteHandle}
                        ref={sizingPaletteAnchorRef}
                        onClose={closeToolbarAndFocusEditor}
                    />
                </Popover>
            </PopoverRoot>
        );
    }
);

/**
 * Toolbar shown for image options.
 */
const ImageOptionsToolbar = React.forwardRef(
    (
        props: ImageOptionsProps & {
            isVisible: boolean;
            onClose: () => void;
            paletteHandle: PaletteHandle;
        },
        ref: React.Ref<HTMLButtonElement>
    ) => {
        const { editor, imageData, isVisible, node, onClose, paletteHandle, caption } = props;

        const theme = useConfigurationTheme();
        const setUserTheme = useSetRecoilState(userThemeAtom);

        React.useEffect(() => {
            const close = (event: KeyboardEvent) => {
                if (event.key === 'Escape' || event.key === 'Tab') {
                    if (paletteHandle.opened) {
                        paletteHandle.close();
                    } else {
                        onClose();
                    }
                    event.preventDefault();
                    event.stopPropagation();
                }
            };
            document.addEventListener('keydown', close, true);
            return () => {
                document.removeEventListener('keydown', close, true);
            };
        }, [paletteHandle, onClose]);

        const isOnlyChild = editor.value.document.getParent(node.key)?.nodes.size === 1 || false;

        return (
            <Toolbar
                className={clsx(
                    'min-w-min flex-wrap transition group-hover/image-block:z-20',
                    isVisible && props.isOnlyNodeSelected
                        ? 'opacity-100'
                        : 'opacity-0 group-hover/image-block:opacity-100'
                )}
            >
                {/* Set image size */}
                <ResizeImageControl {...props} ref={ref} paletteHandle={paletteHandle} />

                {/* Set image alt text */}
                <DialogRoot>
                    <DialogTrigger>
                        <Button label="ALT text" kind="blank" iconOnly leadingIcon={Icon.AltText} />
                    </DialogTrigger>
                    <EditAltTextDialog
                        defaultValue={images.getImageAltText(node) || ''}
                        onChange={(value) => {
                            editor.change((change) => {
                                change.insertDataByKey(node.key, ['alt'], value);
                            });
                        }}
                    />
                </DialogRoot>

                {/* Replace image or set its dark variant */}
                <ReplaceImageButton editor={editor} node={node} />

                {/* Toggle light/dark variant if image has a dark variant */}
                {imageData.refDark ? (
                    <Button
                        label={`Image has a ${
                            theme.color === 'light' ? 'dark' : 'light'
                        } mode alternative.\nToggle theme to see it.`}
                        leadingIcon={theme.color === 'light' ? Icon.ThemeDark : Icon.ThemeLight}
                        onPress={() => {
                            setUserTheme(
                                theme.color === 'light' ? UserTheme.Dark : UserTheme.Light
                            );
                        }}
                    />
                ) : null}

                <Button
                    label="Caption"
                    leadingIcon={Icon.Caption}
                    onPress={() => {
                        onClose();
                        caption.selectCaption();
                    }}
                />

                {caption.hasCaption && (
                    <Button
                        label="Remove caption"
                        leadingIcon={Icon.CaptionRemove}
                        onPress={() => {
                            onClose();
                            caption.removeCaption();
                        }}
                    />
                )}

                {!isOnlyChild && (
                    <Button
                        label="Delete image"
                        leadingIcon={Icon.Trashcan}
                        onPress={() => editor.change((change) => change.removeNodeByKey(node.key))}
                    />
                )}

                {/* Close toolbar */}
                <Button label="Close" leadingIcon={Icon.X} onPress={onClose} />
            </Toolbar>
        );
    }
);

const ResizeImageControl = React.forwardRef(
    (
        props: ToolbarItemProps &
            BlockProps & {
                source: ImageURISource;
                imageData: BlockImageData;
                paletteHandle: PaletteHandle;
            },
        ref: React.Ref<HTMLButtonElement>
    ) => {
        const {
            editor,
            node,
            source,
            imageData: { height },
            paletteHandle,
            ...toolbarItemProps
        } = props;
        const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

        // Sets the size of an image block in the editor, based on the given value.
        const setSize = (value: { size: number; label: string }) => {
            if (!source) {
                return;
            }
            logAnalyticsEvent({
                type: 'edit_page_document_edit_block_property',
                blockType: BLOCK_IMAGE,
                blockProperty: 'size',
                fromValue: undefined,
                toValue: value.label,
            });
            const nextWidth = calculateResizedImageSize(source, value.size);
            editor.change((change) => {
                if (height) {
                    change.removeDataPathByKey(node.key, ['height']);
                }
                if (nextWidth) {
                    change.insertDataByKey(node.key, ['width'], nextWidth);
                } else {
                    change.removeDataPathByKey(node.key, ['width']);
                }
            });
        };

        const paletteCommands = IMAGE_SIZES.map(
            ({ size, label }) =>
                ({
                    key: `${size}`,
                    title: label,
                    type: PaletteCommandType.RADIO,
                    selected: calculateSizeFromDimension(source, node) === size,
                    trigger: () => {
                        setSize({ size, label });
                    },
                }) satisfies PaletteCommandDef
        );

        return (
            <Button
                {...toolbarItemProps}
                label="Size"
                trailingIcon={Icon.ChevronDown}
                iconOnly={false}
                ref={ref}
                onPress={() => {
                    paletteHandle.open(
                        createPalette({
                            position: { width: PalettePositionWidth.ANCHOR },
                            groups: [{ key: 'image.resize', commands: paletteCommands }],
                        })
                    );
                }}
            />
        );
    }
);

/**
 * Image sizes available in the editor.
 */
const IMAGE_SIZES = [
    {
        size: 25,
        label: 'Small',
    },
    {
        size: 50,
        label: 'Medium',
    },
    {
        size: 75,
        label: 'Large',
    },
    {
        size: 100,
        label: 'Fit',
    },
];

/**
 * Calculates the new size of an image based on its source and a percentage size.
 */
function calculateResizedImageSize(source: ImageURISource, size: number): number | undefined {
    const sourceWidth = source.width || spaceLayout.PAGE_BODY_MAX_WIDTH;
    // If the size is set to 100%, it means that the user wants the image to fit the maximum available space.
    // Therefore, we remove any explicitly defined width and allow the image to fit into the available space as much as possible.
    if (size === 100) {
        return;
    }

    // Calculate the width of the image, ensuring that it's between 0 and PAGE_BODY_MAX_WIDTH
    const width = Math.min(sourceWidth, spaceLayout.PAGE_BODY_MAX_WIDTH);

    // Calculate the new size of the image as a percentage of the width, and round it to the nearest integer
    return Math.round(width * (size / 100));
}

function calculateSizeFromDimension(source: ImageURISource, node: Block) {
    const sourceWidth = source.width || spaceLayout.PAGE_BODY_MAX_WIDTH;
    const width = Math.min(sourceWidth, spaceLayout.PAGE_BODY_MAX_WIDTH);
    const nodeData = node.data.get('width');
    return nodeData ? Math.round(100 * (nodeData / width)) : 100;
}
/**
 * This function converts a dimension value or the default value if the dimension is not valid.
 * When using absolute values, the converted dimension will be the actual size in pixels.
 * When using relative values, the converted dimension will be relative to the parent element's size.
 */
function getImageDimension(props: {
    dimension: BlockImageSizeValue | undefined;
    default: string | number;
}): string | number {
    const { dimension, default: defaultValue } = props;

    if (typeof dimension === 'number') {
        return dimension;
    }

    if (typeof dimension === 'object') {
        if (dimension.unit === 'px') {
            return dimension.value;
        }
        return `${dimension.value}${dimension.unit}`;
    }

    return defaultValue;
}

/**
 * Retrieve and resolve image references in the parent node.
 */
async function getParentImagesInContext(
    hiveClient: HiveClient,
    parent: Block | Document | Fragment,
    context: ContentReferenceContext,
    theme: 'light' | 'dark'
): Promise<{ url: string; broken: boolean }[]> {
    const imageRefs = parent.nodes.toArray().map((fileNode) => {
        const { ref, refDark } = fileNode.data.toFrozenObject() as BlockImageData;

        if (theme === 'dark' && refDark) {
            return refDark;
        }

        return ref;
    });

    const brokenImage = { url: '', broken: true };

    const resolvedImages = imageRefs.map((imageRef) =>
        resolveContentRefCache
            .readAsync([hiveClient, imageRef, context])
            .then(([contentReference]) => {
                if (!contentReference) {
                    return brokenImage;
                }

                const url =
                    typeof contentReference.target === 'object'
                        ? resolveNavigationScreenToURL(contentReference.target)
                        : contentReference.target;

                return {
                    url,
                    broken: false,
                };
            })
            .catch(() => brokenImage)
    );

    return Promise.all(resolvedImages);
}
